import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Truck, Settings, LogOut, Bell, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import useAuthStore from '@/store/authStore';
import useUIStore from '@/store/uiStore';
import usePendingUsers from '@/hooks/usePendingUsers';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import useClientStats from '@/hooks/useClientStats';
import { authAPI } from '@/services/api';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { ThemeToggle } from '@/components/ThemeToggle';
import UserProfileModal from '@/components/layout/UserProfileModal';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const DashboardLayout = ({ children, sidebarLinks }) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { isSidebarOpen, toggleSidebar } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { pendingCount, fetchPendingCount, shouldRefresh } = usePendingUsers();
  const { fetchStats: fetchTransporterStats, shouldRefresh: shouldRefreshTransporter } = useTransporterStats();
  const { fetchStats: fetchClientStats, shouldRefresh: shouldRefreshClient } = useClientStats();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);

  // Charger les statistiques appropriées selon le rôle de l'utilisateur
  React.useEffect(() => {
    if (!user) return;

    // Charger les statistiques selon le rôle
    if (['ADMIN', 'AGENT'].includes(user.role) && shouldRefresh) {
      fetchPendingCount();
    } else if (user.role === 'TRANSPORTEUR' && shouldRefreshTransporter) {
      fetchTransporterStats();
    } else if (user.role === 'CLIENT' && shouldRefreshClient) {
      fetchClientStats();
    }
  }, [user, fetchPendingCount, shouldRefresh, fetchTransporterStats, shouldRefreshTransporter, fetchClientStats, shouldRefreshClient]);

  React.useEffect(() => {
    if (isDesktop) {
      setIsMobileSidebarOpen(false);
    }
  }, [isDesktop]);

  const handleLogout = async () => {
    try {
      // Appeler l'API de déconnexion backend pour détruire la session
      await authAPI.logout();
    } catch (error) {
      console.log('Erreur lors de la déconnexion backend:', error);
    } finally {
      // Nettoyer le store frontend
      logout();
      toast({ title: 'Déconnexion réussie' });
      navigate('/');
    }
  };

  const handleNotImplemented = () => {
    toast({
      title: '🚧 Bientôt disponible!',
      description: 'Cette fonctionnalité est en cours de développement.',
    });
  };

  const actualSidebarState = isDesktop ? isSidebarOpen : isMobileSidebarOpen;
  const actualSetSidebarState = isDesktop ? toggleSidebar : () => setIsMobileSidebarOpen(prev => !prev);
  const sidebarWidth = isDesktop ? (isSidebarOpen ? 280 : 80) : 288;
  const showText = isDesktop ? isSidebarOpen : true;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between p-4 h-20`}>
        <Link to="/" className={`flex items-center overflow-hidden ${!isDesktop || isSidebarOpen ? '' : 'justify-center w-full'}`}>
          <img src="/logo.png" alt="LogiConnect" className="h-18 w-auto object-contain flex-shrink-0" />
        </Link>
        {!isDesktop && (
          <Button variant="ghost" size="icon" onClick={() => setIsMobileSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </Button>
        )}
      </div>

      <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
        {sidebarLinks.map((link) => {
          if (link.isHeader) {
            return showText ? (
              <h3 key={link.label} className="px-3 pt-4 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{link.label}</h3>
            ) : null;
          }

          const isAction = !link.to;
          const showBadge = (link.to === '/admin/users' && pendingCount > 0) || (link.badge && link.badge > 0);

          return (
            <Link
              key={link.label}
              to={isAction ? '#' : link.to}
              onClick={(e) => {
                if(isAction) {
                  e.preventDefault();
                  handleNotImplemented();
                }
                if (!isDesktop) setIsMobileSidebarOpen(false);
              }}
              className={`flex items-center p-3 rounded-lg transition-colors relative ${
                !isAction && location.pathname.startsWith(link.to)
                  ? 'bg-secondary text-secondary-foreground font-semibold'
                  : 'hover:bg-accent'
              } ${!showText && 'justify-center'}`}
            >
              <link.icon className="w-5 h-5 flex-shrink-0" />
              {showText && (
                <>
                  <span className="ml-4 whitespace-nowrap flex-1">{link.label}</span>
                  {showBadge && (
                    <span className="ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                      {(link.badge || pendingCount) > 99 ? '99+' : (link.badge || pendingCount)}
                    </span>
                  )}
                </>
              )}
              {!showText && showBadge && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground border-2 border-background">
                  {(link.badge || pendingCount) > 9 ? '9+' : (link.badge || pendingCount)}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-border">
        <Link
          to="/admin/settings"
          onClick={() => !isDesktop && setIsMobileSidebarOpen(false)}
          className={`flex items-center p-3 rounded-lg hover:bg-accent ${!showText && 'justify-center'}`}
        >
          <Settings className="w-5 h-5" />
          {showText && <span className="ml-4 whitespace-nowrap">Paramètres</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`flex items-center w-full p-3 rounded-lg text-destructive hover:bg-destructive/10 hover:text-red-400 ${!showText && 'justify-center'}`}
        >
          <LogOut className="w-5 h-5" />
          {showText && <span className="ml-4 whitespace-nowrap">Déconnexion</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background text-foreground">
      {isDesktop ? (
        <motion.aside
          initial={false}
          animate={{ width: sidebarWidth }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="relative flex-shrink-0 flex-col bg-card border-r border-border hidden lg:flex"
        >
          <SidebarContent />
          <Button variant="ghost" size="icon" className="absolute left-full -translate-x-1/2 top-7 bg-card hover:bg-accent border border-border rounded-full hidden lg:flex" onClick={actualSetSidebarState}>
            {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </Button>
        </motion.aside>
      ) : (
        <AnimatePresence>
          {isMobileSidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
              <motion.aside
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="fixed top-0 left-0 h-full w-72 bg-card border-r border-border z-50 flex flex-col"
              >
                <SidebarContent />
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between h-20 px-4 sm:px-8 bg-card border-b border-border flex-shrink-0">
          <div className="flex items-center">
            {!isDesktop && (
              <Button variant="ghost" size="icon" className="mr-2" onClick={() => setIsMobileSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <ThemeToggle showText={false} />
            <Button variant="ghost" size="icon" className="relative hover:bg-accent">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-secondary ring-2 ring-card" />
            </Button>
            <button
              onClick={() => setIsProfileModalOpen(true)}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer group"
            >
              <Avatar className="w-10 h-10 border-2 border-transparent group-hover:border-primary transition-all flex-shrink-0">
                <AvatarImage src={user?.profile_photo} />
                <AvatarFallback className="bg-secondary text-secondary-foreground font-bold text-sm">
                  {(() => {
                    if (!user) return 'A';
                    const firstChar = user?.first_name?.[0] || user?.name?.[0] || user?.email?.[0] || 'A';
                    return String(firstChar).toUpperCase();
                  })()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block">
                <p className="font-semibold text-foreground">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.first_name || user?.name || 'Utilisateur'}
                </p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role || 'Administrateur'}</p>
              </div>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-background p-4 sm:p-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Modale de profil utilisateur */}
      <UserProfileModal
        open={isProfileModalOpen}
        onOpenChange={setIsProfileModalOpen}
        user={user}
      />
    </div>
  );
};

export default DashboardLayout;
